import React from 'react';
import useIsMobile from '../../../hooks/useIsMobile';

interface ForecastLegendProps {
  forecastType: string;
  maxWindPower?: number;
  selectedVariationChannel?: any;
}

/**
 * A component that displays a legend for the different forecast types
 * with color gradients and value ranges
 */
const ForecastLegend: React.FC<ForecastLegendProps> = ({ forecastType, maxWindPower = 5, selectedVariationChannel }) => {
  const isMobile = useIsMobile();

  // Helper function to determine the title based on forecast type
  const getLegendTitle = () => {
    if (forecastType === 'precipitation' || forecastType === 'hrrr-precipitation' || forecastType === 'graphcast-precipitation') 
      return 'Precipitation (mm)';
    if (forecastType === 'wind10m') 
      return isMobile ? 'Wind (m/s)' : 'Wind Speed (10m) (m/s)';
    if (forecastType === 'wind80m') 
      return isMobile ? 'Wind 80m (m/s)' : 'Wind Speed (80m) (m/s)';
    if (forecastType === 'temperature' || forecastType === 'hrrr-temperature' || forecastType === 'graphcast-temperature') 
      return 'Temperature (°C)';
    if (forecastType === 'humidity' || forecastType === 'hrrr-humidity') 
      return isMobile ? 'Humidity (%)' : 'Relative Humidity (%)';
    if (forecastType === 'solarS') 
      return 'Solar Radiation Short Wave (W/m²)';
    if (forecastType === 'solarL') 
      return 'Solar Radiation Long Wave (W/m²)';
    if (forecastType === 'soilMoisture') 
      return 'Soil Moisture (%)';
    if (forecastType === 'snowWater') 
      return 'Snow Water Equivalent (mm)';
    if (forecastType === 'windPower') 
      return 'Wind Power (MW)';
    if(forecastType.startsWith("processed-")) {
      if(selectedVariationChannel.variation){
        return `${selectedVariationChannel.variation} (${selectedVariationChannel.unit})`
      } else {
        return 'Processed Model'
      }
    }
    return '';
  };

  // Get base forecast type (without hrrr- or graphcast- prefix)
  const getBaseType = () => {
    if (forecastType.startsWith('hrrr-')) return forecastType.substring(5);
    if (forecastType.startsWith('graphcast-')) return forecastType.substring(10);
    return forecastType;
  };

  const baseType = getBaseType();

  // Get legend gradient styles
  const getLegendGradient = () => {

    if(baseType.startsWith('processed-')) {
      return 'linear-gradient(to right, #c8e6c9, #81c784, #ffeb3b, #ffa726, #e53935)'
    }

    switch(baseType) {
      case 'precipitation':
        return 'linear-gradient(to right, #e3f2fd, #90caf9, #42a5f5, #1976d2, #082b61)';
      case 'wind10m':
      case 'wind80m':
      case 'windPower':
        return 'linear-gradient(to right, #c8e6c9, #81c784, #ffeb3b, #ffa726, #e53935)';
      case 'temperature':
        return 'linear-gradient(to right, #b3e5fc, #81d4fa, #4fc3f7, #ffd54f, #ef5350)';
      case 'humidity':
        return 'linear-gradient(to right, #f3e5f5, #ce93d8, #9c27b0, #6a1b9a, #4a148c)';
      case 'solarS':
      case 'solarL':
        return 'linear-gradient(to right, #fff9c4, #fff59d, #ffee58, #fdd835, #f57f17)';
      case 'soilMoisture':
        return 'linear-gradient(to right, #e3f2fd, #90caf9, #42a5f5, #1976d2, #082b61)';
      case 'snowWater':
        return 'linear-gradient(to right, #e3f2fd, #90caf9, #42a5f5, #1976d2, #082b61)';
      default:
        return '';
    }
  };

  // Get legend values based on forecast type
  const renderLegendValues = () => {

    if(baseType.startsWith('processed-')) {
      if(!selectedVariationChannel?.maxValue) {
        return(<div className={`flex justify-between ${isMobile ? 'text-[8px] px-1' : 'text-xs px-1'}`} 
               style={{ width: '100%', minWidth: isMobile ? '200px' : '250px' }}>
            <span className='invisible'>{'_'}</span>
            <span></span>
            <span></span>
            <span></span>
            <span></span>
          </div>)
      }
      return (
          <div className={`flex justify-between ${isMobile ? 'text-[8px] px-1' : 'text-xs px-1'}`} 
               style={{ width: '100%', minWidth: isMobile ? '200px' : '250px' }}>
            <span>0</span>
            <span>{selectedVariationChannel?.maxValue ? Math.round(selectedVariationChannel?.maxValue * 0.25) : 1}</span>
            <span>{selectedVariationChannel?.maxValue ? Math.round(selectedVariationChannel?.maxValue * 0.5) : 3}</span>
            <span>{selectedVariationChannel?.maxValue ? Math.round(selectedVariationChannel?.maxValue * 0.75) : 4}</span>
            <span>{selectedVariationChannel?.maxValue || 5}+</span>
          </div>
        );
    }

    switch(baseType) {
      case 'precipitation':
        return (
          <div className={`flex justify-between ${isMobile ? 'text-[8px] px-1' : 'text-xs px-1'}`}>
            <span>0</span>
            <span>5</span>
            <span>10</span>
            <span>15+</span>
          </div>
        );
      case 'wind10m':
      case 'wind80m':
        return (
          <div className={`flex justify-between ${isMobile ? 'text-[8px] px-1' : 'text-xs px-1'}`}>
            <span>0</span>
            <span>10</span>
            <span>20</span>
            <span>30+</span>
          </div>
        );
      case 'windPower':
        return (
          <div className={`flex justify-between ${isMobile ? 'text-[8px] px-1' : 'text-xs px-1'}`} 
               style={{ width: '100%', minWidth: isMobile ? '200px' : '250px' }}>
            <span>0</span>
            <span>{maxWindPower ? Math.round(maxWindPower * 0.25) : 1}</span>
            <span>{maxWindPower ? Math.round(maxWindPower * 0.5) : 3}</span>
            <span>{maxWindPower ? Math.round(maxWindPower * 0.75) : 4}</span>
            <span>{maxWindPower || 5}+</span>
          </div>
        );
      case 'temperature':
        return (
          <div className={`flex justify-between ${isMobile ? 'text-[8px] px-1' : 'text-xs px-1'}`}>
            <span>-10</span>
            <span>10</span>
            <span>30</span>
            <span>40+</span>
          </div>
        );
      case 'humidity':
        return isMobile ? (
          <div className={`flex justify-between ${isMobile ? 'text-[8px] px-1' : 'text-xs px-1'}`}>
            <span>0%</span>
            <span>50%</span>
            <span>100%</span>
          </div>
        ) : (
          <div className="flex justify-between text-xs px-1">
            <span>0</span>
            <span>25</span>
            <span>50</span>
            <span>75</span>
            <span>100</span>
          </div>
        );
      case 'soilMoisture':
        return isMobile ? (
          <div className={`flex justify-between ${isMobile ? 'text-[8px] px-1' : 'text-xs px-1'}`}>
            <span>0</span>
            <span>50</span>
            <span>100</span>
          </div>
        ) : (
          <div className="flex justify-between text-xs px-1">
            <span>0</span>
            <span>25</span>
            <span>50</span>
            <span>75</span>
            <span>100</span>
          </div>
        );
      case 'snowWater':
        return isMobile ? (
          <div className={`flex justify-between ${isMobile ? 'text-[8px] px-1' : 'text-xs px-1'}`}>
            <span>0</span>
            <span>15</span>
            <span>30</span>
          </div>
        ) : (
          <div className="flex justify-between text-xs px-1">
            <span>0</span>
            <span>10</span>
            <span>20</span>
            <span>30</span>
          </div>
        );
      case 'solarS':
      case 'solarL':
        return isMobile ? (
          <div className={`flex justify-between ${isMobile ? 'text-[8px] px-1' : 'text-xs px-1'}`}>
            <span>0</span>
            <span>500</span>
            <span>1000+</span>
          </div>
        ) : (
          <div className="flex justify-between text-xs px-1">
            <span>0</span>
            <span>250</span>
            <span>500</span>
            <span>750</span>
            <span>1000+</span>
          </div>
        );
      default:
        return null;
    }
  };

  return (
    <div className={`absolute top-${isMobile ? '1' : '2'} left-${isMobile ? '1' : '2'} z-10 bg-white p-3 rounded-md shadow-md`}>
      <div className={`${isMobile ? 'text-[10px]' : 'text-xs'} font-medium mb-${isMobile ? '0' : '1'}`}>
        {getLegendTitle()}
        {!isMobile && forecastType.startsWith('hrrr-') && <span className="ml-1 text-xs font-semibold text-blue-600">(HRRR)</span>}
        {!isMobile && forecastType.startsWith('graphcast-') && <span className="ml-1 text-xs font-semibold text-green-600">(Graphcast)</span>}
      </div>
      
      <div className="flex flex-col w-full">
        <div 
          className={`h-${isMobile ? '2' : '4'} w-full rounded-sm mb-${isMobile ? '0.5' : '1'}`}
          style={{ background: getLegendGradient() }}
        />
        {renderLegendValues()}
      </div>
    </div>
  );
};

export default ForecastLegend;